/**
 * src/pages/DocumentsPage.tsx
 *
 * Affiche le contenu du dossier SharePoint configuré via Microsoft Graph.
 * Utilise le même access token que l'upload PDF dans ReportA4.
 *
 * Fonctionnalités :
 *  - Lister les fichiers et sous-dossiers du dossier racine configuré
 *  - Naviguer dans les sous-dossiers (breadcrumb)
 *  - Ouvrir un fichier dans un nouvel onglet (webUrl)
 *  - Afficher taille, date de modification, icône par extension
 *
 * Pré-requis : user connecté via Microsoft (provider = "microsoft")
 */

import { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "../auth/AuthContext";
import { sharePointConfig } from "../auth/msalConfig";

interface DriveItem {
  id:              string;
  name:            string;
  size?:           number;
  lastModifiedDateTime: string;
  webUrl:          string;
  folder?:         { childCount: number };
  file?:           { mimeType: string };
}

interface BreadcrumbEntry { id: string | null; name: string; }

function fileIcon(item: DriveItem): string {
  if (item.folder) return "📁";
  const ext = item.name.split(".").pop()?.toLowerCase() ?? "";
  const MAP: Record<string, string> = {
    pdf: "📄", doc:"📝", docx:"📝", xls:"📊", xlsx:"📊",
    ppt:"📋", pptx:"📋", png:"🖼", jpg:"🖼", jpeg:"🖼", gif:"🖼",
    mp4:"🎬", mov:"🎬", zip:"🗜", csv:"📊", txt:"📃",
  };
  return MAP[ext] ?? "📄";
}

function fmtSize(bytes?: number): string {
  if (bytes == null) return "—";
  if (bytes < 1024)        return `${bytes} o`;
  if (bytes < 1024 ** 2)   return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 ** 3)   return `${(bytes / 1024 ** 2).toFixed(1)} Mo`;
  return `${(bytes / 1024 ** 3).toFixed(2)} Go`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" });
}

export default function DocumentsPage() {
  const { user, getMicrosoftToken } = useAuthContext();

  // Not configured or not a Microsoft user
  const configured = !!(
    sharePointConfig.siteId &&
    sharePointConfig.driveId &&
    user?.provider === "microsoft"
  );

  const [items,       setItems]     = useState<DriveItem[]>([]);
  const [loading,     setLoading]   = useState(false);
  const [error,       setError]     = useState<string | null>(null);
  const [breadcrumb,  setBreadcrumb]= useState<BreadcrumbEntry[]>([
    { id: null, name: sharePointConfig.folderPath ?? "Documents" },
  ]);

  const currentFolderId = breadcrumb[breadcrumb.length - 1].id;

  const loadFolder = useCallback(async (folderId: string | null) => {
    if (!configured) return;
    setLoading(true); setError(null); setItems([]);
    try {
      const token  = await (getMicrosoftToken as () => Promise<string>)();
      const { siteId, driveId, folderPath } = sharePointConfig;

      // Build Graph URL
      let url: string;
      if (folderId) {
        // Navigate into sub-folder by item id
        url = `https://graph.microsoft.com/v1.0/sites/${encodeURIComponent(siteId)}/drives/${encodeURIComponent(driveId)}/items/${folderId}/children?$orderby=name&$top=200`;
      } else {
        // Root configured folder
        const folder = (folderPath ?? "").replace(/^\/|\/$/g, "");
        url = `https://graph.microsoft.com/v1.0/sites/${encodeURIComponent(siteId)}/drives/${encodeURIComponent(driveId)}/root:/${encodeURIComponent(folder)}:/children?$orderby=name&$top=200`;
      }

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Graph API ${res.status}: ${await res.text()}`);
      const data = await res.json();
      setItems(data.value ?? []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [configured, getMicrosoftToken]);

  useEffect(() => { loadFolder(currentFolderId); }, [loadFolder, currentFolderId]);

  const navigate = (item: DriveItem) => {
    if (!item.folder) {
      window.open(item.webUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setBreadcrumb(prev => [...prev, { id: item.id, name: item.name }]);
  };

  const goTo = (idx: number) => {
    setBreadcrumb(prev => prev.slice(0, idx + 1));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!configured) {
    const isMicrosoft = user?.provider === "microsoft";
    return (
      <div className="screen">
        <div className="screen-inner">
          <h1 className="page-title">Documents</h1>
          <div className="card p-32" style={{ textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📁</div>
            {!isMicrosoft ? (
              <>
                <h2 className="t-title" style={{ fontSize:16, marginBottom:8, color:"var(--text-primary)" }}>
                  Connexion Microsoft requise
                </h2>
                <p style={{ fontSize:13, color:"var(--text-muted)", maxWidth:400, margin:"0 auto" }}>
                  Le dossier SharePoint est accessible uniquement aux utilisateurs connectés via Microsoft Azure AD.
                </p>
              </>
            ) : (
              <>
                <h2 className="t-title" style={{ fontSize:16, marginBottom:8, color:"var(--text-primary)" }}>
                  SharePoint non configuré
                </h2>
                <p style={{ fontSize:13, color:"var(--text-muted)", maxWidth:480, margin:"0 auto 16px" }}>
                  Définissez les variables d'environnement suivantes dans <code>.env.local</code> :
                </p>
                <pre style={{ background:"#f5f5f5", borderRadius:8, padding:"12px 18px", textAlign:"left",
                  fontSize:12, color:"#333", display:"inline-block", maxWidth:480 }}>
{`VITE_SP_SITE_ID=votre-site-id
VITE_SP_DRIVE_ID=votre-drive-id
VITE_SP_FOLDER_PATH=Rapports/2024`}
                </pre>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const folders = items.filter(i => !!i.folder);
  const files   = items.filter(i => !i.folder);

  return (
    <div className="screen">
      <div className="screen-inner">

        {/* Header */}
        <div className="flex-between mb-24">
          <div>
            <h1 className="page-title">Documents</h1>
            <p className="page-subtitle">SharePoint — {sharePointConfig.folderPath}</p>
          </div>
          <button onClick={() => loadFolder(currentFolderId)} className="btn btn-ghost btn-lg"
            title="Rafraîchir" style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:22 }}>↻</span>
          </button>
        </div>

        {/* Breadcrumb */}
        {breadcrumb.length > 1 && (
          <div className="flex gap-4 mb-20" style={{ alignItems:"center", flexWrap:"wrap" }}>
            {breadcrumb.map((crumb, idx) => (
              <span key={idx} className="flex gap-4" style={{ alignItems:"center" }}>
                {idx > 0 && <span style={{ color:"#ccc" }}>›</span>}
                <button onClick={() => goTo(idx)}
                  style={{ background:"none", border:"none", cursor: idx === breadcrumb.length-1 ? "default" : "pointer",
                    fontFamily:"var(--ff-body)", fontSize:13,
                    color: idx === breadcrumb.length-1 ? "var(--text-primary)" : "var(--green-dim)",
                    fontWeight: idx === breadcrumb.length-1 ? 700 : 400, padding:0 }}>
                  {crumb.name}
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="toast toast-err mb-20" style={{ borderRadius:"var(--r-md)" }}>
            <span>⚠ {error}</span>
            <button onClick={() => loadFolder(currentFolderId)}
              style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer",
                color:"var(--red)", textDecoration:"underline", fontFamily:"inherit", fontSize:"inherit" }}>
              Réessayer
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex-col gap-10">
            {[1,2,3,4].map(i => (
              <div key={i} className="card flex gap-14" style={{ padding:"14px 18px", alignItems:"center" }}>
                <div style={{ width:32, height:32, background:"#f0f0f0", borderRadius:6 }}/>
                <div className="flex-1">
                  <div style={{ background:"#f0f0f0", height:13, width:"40%", borderRadius:4, marginBottom:6 }}/>
                  <div style={{ background:"#f5f5f5", height:10, width:"25%", borderRadius:4 }}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && items.length === 0 && (
          <div className="card p-32" style={{ textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📂</div>
            <p style={{ fontSize:13, color:"var(--text-muted)" }}>Ce dossier est vide.</p>
          </div>
        )}

        {/* Items — folders first, then files */}
        {!loading && !error && items.length > 0 && (
          <div className="flex-col gap-8">
            {/* Section dossiers */}
            {folders.map(item => (
              <button key={item.id} onClick={() => navigate(item)}
                className="card flex gap-14"
                style={{ padding:"12px 18px", alignItems:"center", background:"#fff",
                  border:"1.5px solid var(--border-light)", cursor:"pointer", textAlign:"left",
                  width:"100%", transition:"border-color 0.15s" }}
                onMouseEnter={e=>(e.currentTarget.style.borderColor="var(--green-dim)")}
                onMouseLeave={e=>(e.currentTarget.style.borderColor="var(--border-light)")}>
                <span style={{ fontSize:26, flexShrink:0 }}>📁</span>
                <div className="flex-1 overflow-h">
                  <div className="t-title" style={{ fontSize:14, color:"var(--text-primary)",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>
                    {item.folder?.childCount ?? 0} élément{(item.folder?.childCount ?? 0) !== 1 ? "s" : ""}
                  </div>
                </div>
                <span style={{ fontSize:16, color:"#ccc" }}>›</span>
              </button>
            ))}

            {/* Separator */}
            {folders.length > 0 && files.length > 0 && (
              <div style={{ height:1, background:"var(--border-light)", margin:"4px 0" }}/>
            )}

            {/* Section fichiers */}
            {files.map(item => (
              <button key={item.id} onClick={() => navigate(item)}
                className="card flex gap-14"
                style={{ padding:"12px 18px", alignItems:"center", background:"#fff",
                  border:"1.5px solid var(--border-light)", cursor:"pointer", textAlign:"left",
                  width:"100%", transition:"border-color 0.15s" }}
                onMouseEnter={e=>(e.currentTarget.style.borderColor="var(--green-dim)")}
                onMouseLeave={e=>(e.currentTarget.style.borderColor="var(--border-light)")}>
                <span style={{ fontSize:24, flexShrink:0 }}>{fileIcon(item)}</span>
                <div className="flex-1 overflow-h">
                  <div className="t-title" style={{ fontSize:13, color:"var(--text-primary)",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2, display:"flex", gap:12 }}>
                    <span>{fmtSize(item.size)}</span>
                    <span>Modifié le {fmtDate(item.lastModifiedDateTime)}</span>
                  </div>
                </div>
                <span style={{ fontSize:11, color:"var(--green-dim)", textTransform:"uppercase",
                  letterSpacing:0.5, fontFamily:"var(--ff-title)", fontWeight:700, flexShrink:0 }}>
                  Ouvrir
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
